import React from 'react';

import { createRoot } from 'react-dom/client';
import PixelBlast from './PixelBlast';

const BASE_PROPS = {
  variant: 'circle',
  transparent: true,
  enableRipples: false,
};

const STATE_KEY = '__pixelBlastState';
const DEFAULT_MODE = 'ambient';
const MODE_PROPS = {
  focus: {
    pixelSize: 8,
    patternScale: 2.4,
    patternDensity: 0.68,
    pixelSizeJitter: 0.16,
    liquid: false,
    speed: 0.12,
    edgeFade: 0.38,
  },
  ambient: {
    pixelSize: 7,
    patternScale: 2.7,
    patternDensity: 0.8,
    pixelSizeJitter: 0.22,
    liquid: false,
    speed: 0.18,
    edgeFade: 0.32,
  },
  feature: {
    pixelSize: 6,
    patternScale: 2.95,
    patternDensity: 0.9,
    pixelSizeJitter: 0.28,
    liquid: false,
    speed: 0.24,
    edgeFade: 0.26,
  },
};

const readThemeColor = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return '#7EA6DF';
  }

  const value = getComputedStyle(document.documentElement)
    .getPropertyValue('--pixelblast-color')
    .trim();

  return value || '#7EA6DF';
};

const readMode = (container) => {
  const mode = container?.dataset?.pixelblastMode?.trim();
  return MODE_PROPS[mode] ? mode : DEFAULT_MODE;
};

const buildProps = (container) => ({
  ...BASE_PROPS,
  ...(MODE_PROPS[readMode(container)] || MODE_PROPS[DEFAULT_MODE]),
  color: readThemeColor(),
});

const mountPixelBlast = () => {
  const container = document.getElementById('pixel-blast-root');
  if (!container) return;

  const previousState = window[STATE_KEY];
  if (previousState && typeof previousState.cleanup === 'function') {
    try {
      previousState.cleanup();
    } catch (error) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('PixelBlast cleanup failed before remounting', error);
      }
    }
    if (previousState.beforeUnload) {
      window.removeEventListener('beforeunload', previousState.beforeUnload);
    }
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = createRoot(container);
  let cleanedUp = false;
  const themeObserver = new MutationObserver(() => render());

  const render = () => {
    if (prefersReducedMotion.matches) {
      container.style.display = 'none';
      root.render(null);
    } else {
      container.style.display = '';
      root.render(<PixelBlast {...buildProps(container)} />);
    }
  };

  render();

  const handlePreferenceChange = () => render();
  prefersReducedMotion.addEventListener('change', handlePreferenceChange);
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });
  themeObserver.observe(container, {
    attributes: true,
    attributeFilter: ['data-pixelblast-mode'],
  });

  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    prefersReducedMotion.removeEventListener('change', handlePreferenceChange);
    themeObserver.disconnect();
    try {
      root.unmount();
    } catch (error) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('PixelBlast root unmount failed', error);
      }
    }
    container.style.display = '';
    if (container.firstChild) {
      container.replaceChildren();
    }
    window[STATE_KEY] = undefined;
  };

  const handleBeforeUnload = () => cleanup();

  window.addEventListener('beforeunload', handleBeforeUnload, { once: true });

  window[STATE_KEY] = {
    cleanup,
    beforeUnload: handleBeforeUnload,
  };
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountPixelBlast, { once: true });
} else {
  mountPixelBlast();
}
